import {
    Banknote,
    Building2,
    CreditCard,
    Hand,
    Landmark,
    Scissors,
    ShoppingBag,
    Smartphone,
    Zap,
} from 'lucide-react';
import {z} from 'zod';

// =============================================
// COMMON TYPES
// =============================================

// Salão entity - used for product sales that go directly to the salon
export const SALAO_EMPLOYEE = {
    id: '2be7baba-b5fe-4495-a786-a51eecec3710',
    name: 'Salão',
} as const;

export const userRoleOptions = z.enum(['receptionist', 'employee', 'owner']);

export type UserRole = z.infer<typeof userRoleOptions>;

export const departmentOptions = z.enum(['Cabeleireiro', 'Manicure', 'Depilação', 'Produto']);

export type Department = z.infer<typeof departmentOptions>;

export const DEPARTMENT_ICONS: Record<Department, typeof Scissors> = {
    [departmentOptions.enum.Cabeleireiro]: Scissors,
    [departmentOptions.enum.Manicure]: Hand,
    [departmentOptions.enum.Depilação]: Zap,
    [departmentOptions.enum.Produto]: ShoppingBag,
};

// Map department to employee departamento field
export const DEPARTMENT_TO_EMPLOYEE_DEPT: Record<Department, string | null> = {
    [departmentOptions.enum.Cabeleireiro]: departmentOptions.enum.Cabeleireiro,
    [departmentOptions.enum.Manicure]: departmentOptions.enum.Manicure,
    [departmentOptions.enum.Depilação]: departmentOptions.enum.Depilação,
    [departmentOptions.enum.Produto]: null, // Products can be sold by anyone
};

export const DEPARTMENTS: {id: Department; label: string}[] = [
    {
        id: departmentOptions.enum.Cabeleireiro,
        label: departmentOptions.enum.Cabeleireiro,
    },
    {id: departmentOptions.enum.Manicure, label: departmentOptions.enum.Manicure},
    {id: departmentOptions.enum.Depilação, label: departmentOptions.enum.Depilação},
    {id: departmentOptions.enum.Produto, label: departmentOptions.enum.Produto},
];

// =============================================
// API RESPONSE TYPES
// =============================================

export const apiResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

export type ApiResponse = z.infer<typeof apiResponseSchema>;

// =============================================
// PAYMENT TYPES
// =============================================

export const PAYMENT_METHOD = {
    DINHEIRO: 'Dinheiro',
    PIX: 'PIX',
    DEBITO: 'Débito',
    CREDITO: 'Crédito',
} as const;

export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

const paymentMethodValues = Object.values(PAYMENT_METHOD) as [PaymentMethod, ...PaymentMethod[]];
export const paymentMethodOptions = z.enum(paymentMethodValues);

// Payment methods with Lucide icons
export const PAYMENT_METHODS = [
    {id: PAYMENT_METHOD.DINHEIRO, label: PAYMENT_METHOD.DINHEIRO, icon: Banknote},
    {id: PAYMENT_METHOD.PIX, label: PAYMENT_METHOD.PIX, icon: Smartphone},
    {id: PAYMENT_METHOD.DEBITO, label: PAYMENT_METHOD.DEBITO, icon: CreditCard},
    {id: PAYMENT_METHOD.CREDITO, label: PAYMENT_METHOD.CREDITO, icon: CreditCard},
] as const;

export const PAYMENT_FEES: Record<string, number> = {
    [PAYMENT_METHOD.DINHEIRO]: 0,
    [PAYMENT_METHOD.PIX]: 0,
    [PAYMENT_METHOD.DEBITO]: 0.0144, // 1.44%
    [PAYMENT_METHOD.CREDITO]: 0.0244, // 2.44%
};

export const getPaymentFee = (paymentMethod: string): number => {
    return PAYMENT_FEES[paymentMethod] ?? 0;
};

// =============================================
// PERIOD/DATE TYPES
// =============================================

export type PeriodMode = 'semanal' | 'mensal';

// =============================================
// VAULT/COFRE TYPES
// =============================================

export const VAULT = {
    DINHEIRO: 'Dinheiro',
    BANCO_BRASIL: 'Banco do Brasil',
    BANCO_ITAU: 'Banco Itaú',
    INFINITE_PAY: 'InfinitePay',
} as const;

export type Vault = (typeof VAULT)[keyof typeof VAULT];

export const VAULTS = [
    {id: VAULT.DINHEIRO, label: VAULT.DINHEIRO, icon: Banknote, color: 'var(--vault-dinheiro)'},
    {id: VAULT.BANCO_BRASIL, label: VAULT.BANCO_BRASIL, icon: Landmark, color: 'var(--vault-bb)'},
    {id: VAULT.BANCO_ITAU, label: VAULT.BANCO_ITAU, icon: Building2, color: 'var(--vault-itau)'},
    {
        id: VAULT.INFINITE_PAY,
        label: VAULT.INFINITE_PAY,
        icon: CreditCard,
        color: 'var(--vault-infinite)',
    },
] as const;
